<template>
  <b-container class="my-4">
    <h2 class="mb-4">Uso de servicios</h2>

    <!-- Logs -->
    <b-card class="mb-4" title="Registro de usos">
      <b-row class="mb-2">
        <b-col md="3">
          <label>Desde</label>
          <b-form-input v-model="filters.start" type="date" />
        </b-col>
        <b-col md="3">
          <label>Hasta</label>
          <b-form-input v-model="filters.end" type="date" />
        </b-col>
        <b-col md="3">
          <label>Usuario</label>
          <multiselect
            v-model="filters.user"
            label="email"
            track-by="id"
            placeholder="Todos"
            :options="users"
          />
        </b-col>
        <b-col md="3">
          <label>Servicio</label>
          <b-form-select v-model="filters.product">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option
              v-for="s in services"
              :key="s.code"
              :value="s.code"
              >{{ s.label }}</b-form-select-option
            >
          </b-form-select>
        </b-col>
      </b-row>
      <b-button variant="primary" :disabled="loadingLogs" @click="loadLogs"
        >Filtrar</b-button
      >
      <span class="ml-3 text-muted">{{ logs.length }} resultados</span>

      <span v-if="logs.length === LIMITE" class="ml-2 text-warning">
        (tope alcanzado: puede haber más, acota las fechas)
      </span>

      <b-table
        class="mt-3"
        small
        striped
        :items="logs"
        :fields="logFields"
        :busy="loadingLogs"
        :per-page="perPage"
        :current-page="logPage"
        show-empty
        empty-text="Sin registros"
      />
      <b-pagination
        v-if="logs.length > perPage"
        v-model="logPage"
        :total-rows="logs.length"
        :per-page="perPage"
        align="center"
        class="mb-0"
      />
    </b-card>

    <!-- Usuarios nuevos sin colegio pagado: los que gastan saldo -->
    <b-card class="mb-4" title="Usuarios nuevos">
      <p class="text-muted">
        Usuarios sin colegio con contrato vigente, que son los que gastan usos.
        Tener colegio no los excluye: quien probó la demo de Eureka arrastra uno
        sin pagar.
      </p>
      <b-row class="mb-2">
        <b-col md="3">
          <label>Desde</label>
          <b-form-input v-model="newUserFilters.start" type="date" />
        </b-col>
        <b-col md="3">
          <label>Hasta</label>
          <b-form-input v-model="newUserFilters.end" type="date" />
        </b-col>
      </b-row>
      <b-button
        variant="primary"
        :disabled="loadingNewUsers"
        @click="loadNewUsers"
        >Filtrar</b-button
      >
      <!-- La descarga usa las MISMAS fechas del filtro de arriba: el archivo
           que se baja es el periodo que se esta mirando, no otro. Que sea un
           solo boton y no uno por tabla es a proposito: lo que se comparte es
           el archivo entero, y asi queda claro que es una sola cosa. -->
      <b-button
        class="ml-2"
        variant="outline-success"
        :disabled="exportando"
        @click="exportarMarketing"
      >
        <b-spinner v-if="exportando" small class="mr-1" />
        Descargar Excel para análisis (sin datos personales)
      </b-button>
      <span class="ml-3 text-muted">{{ newUsers.length }} resultados</span>

      <span v-if="newUsers.length === LIMITE" class="ml-2 text-warning">
        (tope alcanzado: puede haber más, acota las fechas)
      </span>

      <b-table
        class="mt-3"
        small
        striped
        responsive
        :items="newUsers"
        :fields="newUserFields"
        :busy="loadingNewUsers"
        :per-page="perPage"
        :current-page="newUserPage"
        show-empty
        empty-text="Sin usuarios en el período"
      />
      <p class="text-muted small mb-0">
        El Excel trae seis hojas —cuentas, usos, compras, cotizaciones,
        colegios e invitaciones— con ids y conteos, y una séptima que explica
        cada columna. Sin correos, sin nombres, sin estudiantes: es el archivo
        que se puede pasar para análisis. Sin tope de filas, a diferencia de
        las tablas de esta pantalla.
      </p>
      <b-pagination
        v-if="newUsers.length > perPage"
        v-model="newUserPage"
        :total-rows="newUsers.length"
        :per-page="perPage"
        align="center"
        class="mb-0"
      />
    </b-card>

    <!-- Transacciones -->
    <b-card class="mb-4" title="Transacciones">
      <b-row class="mb-2">
        <b-col md="3">
          <label>Desde</label>
          <b-form-input v-model="txFilters.start" type="date" />
        </b-col>
        <b-col md="3">
          <label>Hasta</label>
          <b-form-input v-model="txFilters.end" type="date" />
        </b-col>
        <b-col md="3">
          <label>Email</label>
          <b-form-input
            v-model="txFilters.email"
            placeholder="Parte del correo"
            @keyup.enter="loadTransactions"
          />
        </b-col>
      </b-row>
      <b-button
        variant="primary"
        :disabled="loadingTx"
        @click="loadTransactions"
        >Filtrar</b-button
      >
      <span class="ml-3 text-muted">{{ transactions.length }} resultados</span>

      <span v-if="transactions.length === LIMITE" class="ml-2 text-warning">
        (tope alcanzado: puede haber más, acota las fechas)
      </span>

      <b-table
        class="mt-3"
        small
        striped
        responsive
        :items="transactions"
        :fields="txFields"
        :busy="loadingTx"
        :per-page="perPage"
        :current-page="txPage"
        show-empty
        empty-text="Sin transacciones en el período"
      >
        <template #cell(estado)="row">
          <b-badge :variant="ESTADOS[row.item.estado].variant">{{
            ESTADOS[row.item.estado].label
          }}</b-badge>
          <!-- Las rechazadas de antes de que se guardara el motivo no lo
               tienen: se muestran sin motivo. -->
          <small
            v-if="row.item.motivo_rechazo"
            class="d-block text-muted text-truncate"
            style="max-width: 16rem"
            :title="row.item.motivo_rechazo"
            >{{ row.item.motivo_rechazo }}</small
          >
        </template>
        <!-- Con folio se puede verificar contra el SII; aprobada sin folio es
             una venta cobrada sin documento y se puede reintentar desde aca. -->
        <template #cell(dte)="row">
          <template v-if="row.item.dte_folio">
            <b-badge variant="success">Folio {{ row.item.dte_folio }}</b-badge>
            <b-button
              size="sm"
              variant="link"
              class="p-0 ml-2"
              :disabled="row.item.cargandoTrazas"
              @click="verificarDte(row)"
              >Verificar</b-button
            >
          </template>
          <b-badge
            v-else-if="row.item.estado === 'aprobada' && row.item.dte_encolado"
            variant="info"
            >En cola</b-badge
          >
          <template v-else-if="row.item.estado === 'aprobada'">
            <b-badge variant="danger">Sin DTE</b-badge>
            <small
              v-if="row.item.dte_error"
              class="d-block text-muted text-truncate"
              style="max-width: 16rem"
              :title="row.item.dte_error"
              >{{ row.item.dte_error }}</small
            >
            <b-button
              size="sm"
              variant="outline-danger"
              class="mt-1"
              :disabled="row.item.emitiendo"
              @click="emitirDte(row.item)"
              >Emitir DTE</b-button
            >
          </template>
          <span v-else>—</span>
        </template>
        <template #row-details="row">
          <div v-if="row.item.trazas">
            <p v-if="row.item.trazas.error" class="text-danger mb-0">
              {{ row.item.trazas.error }}
            </p>
            <p v-else-if="!row.item.trazas.lista.length" class="mb-0">
              El SII aún no reporta trazas para este folio.
            </p>
            <ul v-else class="mb-0">
              <li v-for="(t, i) in row.item.trazas.lista" :key="i">
                {{ t.fecha }}: {{ t.descripcion }}
              </li>
            </ul>
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="transactions.length > perPage"
        v-model="txPage"
        :total-rows="transactions.length"
        :per-page="perPage"
        align="center"
        class="mb-0"
      />
    </b-card>

    <!-- Manage a single user's uses -->
    <b-card class="mb-4" title="Usos de un usuario">
      <b-row class="mb-2">
        <b-col md="6">
          <label>Usuario</label>
          <multiselect
            v-model="selectedUser"
            label="email"
            track-by="id"
            placeholder="Selecciona un usuario"
            :options="users"
            @input="loadUserUses"
          />
        </b-col>
      </b-row>
      <b-row v-if="userUses">
        <b-col
          v-for="s in editableServices"
          :key="s.field"
          md="3"
          class="mb-2"
        >
          <label>{{ s.label }}</label>
          <b-form-input v-model.number="userUses[s.field]" type="number" />
        </b-col>
      </b-row>
      <b-button
        v-if="userUses"
        variant="primary"
        @click="saveUserUses"
        >Guardar</b-button
      >
    </b-card>

    <!-- Grant uses to all users -->
    <b-card title="Regalar usos a todos los usuarios">
      <b-row class="mb-2">
        <b-col md="4">
          <label>Servicio</label>
          <b-form-select v-model="grant.field">
            <b-form-select-option
              v-for="s in editableServices"
              :key="s.field"
              :value="s.field"
              >{{ s.label }}</b-form-select-option
            >
          </b-form-select>
        </b-col>
        <b-col md="4">
          <label>Cantidad</label>
          <b-form-input v-model.number="grant.amount" type="number" />
        </b-col>
      </b-row>
      <b-button variant="warning" @click="grantAll">Regalar a todos</b-button>
    </b-card>
  </b-container>
</template>

<script>
import informationService from "../../../services/informationService";
import serviceLogService from "../../../services/serviceLogService";
import transactionService from "../../../services/transactionService";

// Fixed service catalog. `field` is the User uses column (null = school-scoped
// or log-only alias sharing another product's balance).
// MATERIAL e INFORMAL comparten materials_uses: el saldo se edita una vez;
// INFORMAL solo filtra el registro de usos.
const SERVICES = [
  { code: "PLANI", label: "Planificaciones", field: "planifications_uses" },
  { code: "MATERIAL", label: "Materiales", field: "materials_uses" },
  { code: "INFORMAL", label: "Pruebas informales", field: null },
  { code: "DOCUMENT", label: "Documentos", field: "documents_uses" },
  { code: "TEST", label: "Corrección de Tests", field: "tests_uses" },
  { code: "EUR_STU", label: "Eureka", field: null },
];

// Se pagina en el cliente: los dos endpoints ya devuelven como mucho LIMITE
// filas, asi que la tanda entera esta en memoria y `b-table` la corta sola con
// :per-page/:current-page. Paginar en el servidor seria repetir offset, total y
// re-fetch en dos endpoints para un tope que no llega a un mega.
const POR_PAGINA = 25;

// Los estados que devuelve `transactions/analisis/`. "Sin terminar" es tambien
// donde caen las compras anteriores al registro del estado: de esas no se sabe
// si las rechazaron o si las abandonaron, y no se inventa.
const ESTADOS = {
  aprobada: { label: "Aprobada", variant: "success" },
  rechazada: { label: "Rechazada", variant: "danger" },
  sin_terminar: { label: "Sin terminar", variant: "secondary" },
};

// El tope de filas de `list` y `new-users` (backend/api/viewsets/transactions.py).
// Solo sirve para avisar que la tanda vino cortada: paginar una lista truncada
// sin decirlo es como se lee "llegue al final, entonces son todos".
const LIMITE = 2000;

// Las fuentes que se escriben largo y salen en casi todas las filas. Es una
// abreviatura de pantalla y nada mas: en el Excel y en el `title` de la celda
// va el valor tal cual vino del anuncio.
const FUENTE_CORTA = {
  instagram: "ig",
  facebook: "fb",
  google: "goog",
};

// Los ultimos digitos del id del anuncio. Con eso alcanza para distinguir dos
// filas de un vistazo; el id entero va en el title, que es de donde se copia.
const COLA_ANUNCIO = 4;

// La fecha local en el formato que pide <input type="date">.
//
// `toISOString()` da UTC: en Chile, a partir de las 21:00, devuelve el dia
// siguiente y el filtro "hasta hoy" deja fuera lo de hoy.
function isoLocal(date) {
  const dos = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${dos(date.getMonth() + 1)}-${dos(
    date.getDate()
  )}`;
}

// El nombre del archivo que mando el servidor. Si el header no viaja —un
// proxy que lo recorte, por ejemplo— se usa el de respaldo en vez de dejar al
// navegador inventando un "download" sin extension.
function nombreDeLaRespuesta(response, respaldo) {
  const cabecera = response.headers["content-disposition"] || "";
  const encontrado = cabecera.match(/filename[^;=\n]*=(?:UTF-8'')?"?([^";\n]*)"?/);
  return encontrado ? decodeURIComponent(encontrado[1]) : respaldo;
}

// Los ultimos 30 dias, que es lo que la tabla muestra al abrir.
function ultimosTreintaDias() {
  const hoy = new Date();
  const desde = new Date();
  desde.setDate(desde.getDate() - 30);
  return { start: isoLocal(desde), end: isoLocal(hoy) };
}

export default {
  created() {
    this.getUsers();
    this.loadLogs();
    this.loadNewUsers();
    this.loadTransactions();
  },
  data() {
    return {
      services: SERVICES,
      users: [],
      filters: { start: "", end: "", user: null, product: null },
      logs: [],
      loadingLogs: false,
      logPage: 1,
      perPage: POR_PAGINA,
      LIMITE,
      logFields: [
        { key: "created_at", label: "Fecha", formatter: this.formatDate },
        { key: "user_email", label: "Usuario" },
        { key: "user_full_name", label: "Nombre" },
        { key: "product_name", label: "Servicio" },
        this.columnaOrigen(),
      ],
      newUserFilters: ultimosTreintaDias(),
      newUsers: [],
      loadingNewUsers: false,
      newUserPage: 1,
      exportando: false,
      ESTADOS,
      txFilters: ultimosTreintaDias(),
      transactions: [],
      loadingTx: false,
      txPage: 1,
      txFields: [
        { key: "created_at", label: "Fecha", formatter: this.formatDate },
        { key: "email", label: "Usuario" },
        { key: "nombre", label: "Nombre", formatter: this.formatName },
        { key: "estado", label: "Estado" },
        { key: "dte", label: "DTE" },
        { key: "product_name", label: "Servicio" },
        { key: "amount", label: "Usos" },
        { key: "total", label: "Total", formatter: this.formatPesos },
      ],
      selectedUser: null,
      userUses: null,
      grant: { field: "planifications_uses", amount: 0 },
    };
  },
  computed: {
    editableServices() {
      return this.services.filter((s) => s.field);
    },
    // Una columna de saldo por servicio editable. Salen del mismo SERVICES que
    // el resto de la vista, asi que agregar un producto no deja esta tabla
    // corta. INFORMAL no aparece: comparte materials_uses con MATERIAL y seria
    // la misma columna dos veces.
    newUserFields() {
      return [
        { key: "nombre", label: "Nombre", formatter: this.formatName },
        { key: "email", label: "Email" },
        { key: "created_at", label: "Registro", formatter: this.formatDate },
        ...this.editableServices.map((s) => ({
          key: s.field,
          label: s.label,
        })),
        // Eureka va aparte: su saldo vive en el colegio y no en el usuario, asi
        // que no esta en editableServices (su `field` es null) y llega
        // anotado desde el backend.
        {
          key: "eureka_uses",
          label: "Eureka",
          formatter: this.formatEurekaUses,
        },
        this.columnaOrigen(true),
        { key: "invitados", label: "Invitó a" },
        {
          key: "fue_invitado",
          label: "¿Lo invitaron?",
          formatter: (value) => (value ? "Sí" : "No"),
        },
      ];
    },
  },
  methods: {
    formatDate(value) {
      return value ? new Date(value).toLocaleString("es-CL") : "";
    },
    // Sin colegio no hay saldo de Eureka que mostrar: el guion dice "no
    // aplica", que no es lo mismo que un cero.
    formatEurekaUses(value) {
      return value === null || value === undefined ? "—" : value;
    },
    // El nombre es opcional: se registra con Google y muchos no lo traen.
    formatName(value, key, item) {
      return [item.first_name, item.last_name].filter(Boolean).join(" ") || "—";
    },
    // De donde vino la cuenta. La misma columna en las dos tablas —quien se
    // registro y quien uso algo—, definida una sola vez: si manana se le
    // agrega un dato al resumen, no hay una segunda copia que se quede vieja.
    //
    // Cabe en una celda porque solo lleva lo que se compara de un vistazo: el
    // detalle completo esta en el title y, entero, en el Excel. Las dos
    // respuestas del backend traen el MISMO objeto `origen`
    // (OrigenResumenSerializer), que es lo que permite compartir la columna.
    //
    // `conProducto` es lo unico que cambia entre las dos: en "Registro de
    // usos" cada fila YA es un producto —el que se uso— y poner al lado el
    // producto por el que se creo la cuenta se lee como si fuera el mismo.
    columnaOrigen(conProducto = false) {
      return {
        key: "origen",
        label: "Origen",
        formatter: this.formatOrigen,
        tdAttr: (value, key, item) => this.origenTitulo(item, conProducto),
      };
    },
    // "google · ig · anuncio 1203 · app": metodo, fuente, cola del id del
    // anuncio y, si corresponde, que el alta ocurrio dentro del navegador de
    // otra app. Las cuentas anteriores al 7-sep-2026 no tienen fila de origen
    // —el backend responde `origen: null`— y muestran un guion: no es que no
    // tuvieran campana, es que nadie la escribia.
    formatOrigen(origen) {
      if (!origen || !origen.metodo) return "—";
      const partes = [origen.metodo];
      const fuente = origen.utm_source;
      if (fuente) partes.push(FUENTE_CORTA[fuente.toLowerCase()] || fuente);
      if (origen.utm_content)
        partes.push(`anuncio ${origen.utm_content.slice(-COLA_ANUNCIO)}`);
      if (origen.navegador_interno) partes.push("app");
      return partes.join(" · ");
    },
    // El id del anuncio completo, para copiarlo. Va en el title del <td> y no
    // en la celda porque un utm_content son 20 y pico de caracteres que
    // desarman la tabla.
    origenTitulo(item, conProducto) {
      const origen = item.origen;
      if (!origen || !origen.metodo) {
        return {
          title: "Cuenta anterior al registro de origen (7-sep-2026)",
        };
      }
      const lineas = [
        `método: ${origen.metodo}`,
        conProducto && origen.producto ? `producto: ${origen.producto}` : null,
        origen.utm_source ? `utm_source: ${origen.utm_source}` : null,
        origen.utm_content ? `utm_content: ${origen.utm_content}` : null,
        origen.navegador_interno
          ? "el alta ocurrió dentro del navegador de otra app"
          : null,
      ].filter(Boolean);
      return { title: lineas.join("\n") };
    },
    formatPesos(value) {
      return value === null || value === undefined
        ? "—"
        : value.toLocaleString("es-CL", { style: "currency", currency: "CLP" });
    },
    loadTransactions() {
      this.loadingTx = true;
      const params = { ...this.txFilters };
      if (!params.email) delete params.email;
      transactionService
        .getAnalisis(params)
        .then((data) => {
          this.transactions = data;
          this.txPage = 1;
        })
        .catch(() =>
          this.$toasted.error("No se pudieron cargar las transacciones")
        )
        .finally(() => {
          this.loadingTx = false;
        });
    },
    // El mensaje del backend si lo mando; si no, el generico.
    mensajeDeError(err, generico) {
      const data = err && err.response && err.response.data;
      return (data && data.message) || generico;
    },
    // Las trazas se piden una vez por fila y se cachean en el item; el segundo
    // click solo pliega/despliega. `$set` porque el item llega del backend sin
    // esas claves y Vue 2 no ve las que se agregan despues.
    verificarDte(row) {
      if (row.item.trazas) {
        row.toggleDetails();
        return;
      }
      this.$set(row.item, "cargandoTrazas", true);
      transactionService
        .getDteTrazas(row.item.id)
        .then((data) => {
          this.$set(row.item, "trazas", { lista: data.trazas || [] });
        })
        .catch((err) => {
          this.$set(row.item, "trazas", {
            error: this.mensajeDeError(err, "No se pudieron cargar las trazas"),
          });
        })
        .finally(() => {
          this.$set(row.item, "cargandoTrazas", false);
          row.toggleDetails();
        });
    },
    emitirDte(item) {
      this.$bvModal
        .msgBoxConfirm(
          `¿Emitir el DTE de la compra ${item.id} (${item.email})?`,
          { okTitle: "Emitir", cancelTitle: "Cancelar", okVariant: "danger" }
        )
        .then((ok) => {
          if (!ok) return;
          this.$set(item, "emitiendo", true);
          return transactionService
            .emitirDte(item.id)
            .then(() => {
              this.$toasted.success("DTE encolado");
              this.$set(item, "dte_encolado", true);
            })
            .catch((err) =>
              this.$toasted.error(
                this.mensajeDeError(err, "No se pudo encolar el DTE")
              )
            )
            .finally(() => {
              this.$set(item, "emitiendo", false);
            });
        });
    },
    // Baja el Excel con las MISMAS fechas del filtro de esta tarjeta.
    //
    // El nombre lo pone el servidor en content-disposition: si algun dia el
    // rango se ajusta del lado del backend, el archivo se llama como lo que
    // trae de verdad y no como lo que se pidio.
    exportarMarketing() {
      this.exportando = true;
      serviceLogService
        .exportarMarketing({
          desde: this.newUserFilters.start,
          hasta: this.newUserFilters.end,
        })
        .then((response) => {
          const nombre = nombreDeLaRespuesta(
            response,
            `utopie-marketing-${this.newUserFilters.start}-${this.newUserFilters.end}.xlsx`
          );
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          const enlace = document.createElement("a");
          enlace.href = window.URL.createObjectURL(blob);
          enlace.download = nombre;
          enlace.click();
          window.URL.revokeObjectURL(enlace.href);
        })
        .catch(() => this.$toasted.error("No se pudo generar el Excel"))
        .finally(() => {
          this.exportando = false;
        });
    },
    loadNewUsers() {
      this.loadingNewUsers = true;
      serviceLogService
        .getNewUsers({ ...this.newUserFilters })
        .then((data) => {
          this.newUsers = data;
          this.newUserPage = 1;
        })
        .catch(() => this.$toasted.error("No se pudieron cargar los usuarios"))
        .finally(() => {
          this.loadingNewUsers = false;
        });
    },
    getUsers() {
      informationService.getUsers().then((data) => {
        this.users = data.users;
      });
    },
    loadLogs() {
      this.loadingLogs = true;
      const params = {};
      if (this.filters.start) params.start = this.filters.start;
      if (this.filters.end) params.end = this.filters.end;
      if (this.filters.user) params.user = this.filters.user.id;
      if (this.filters.product) params.product = this.filters.product;
      serviceLogService
        .getLogs(params)
        .then((data) => {
          this.logs = data;
          // Sin esto, filtrar desde la pagina 7 deja la tabla en blanco cuando
          // el resultado nuevo tiene dos paginas.
          this.logPage = 1;
        })
        .catch(() => this.$toasted.error("No se pudieron cargar los registros"))
        .finally(() => {
          this.loadingLogs = false;
        });
    },
    loadUserUses() {
      if (!this.selectedUser) {
        this.userUses = null;
        return;
      }
      serviceLogService.getUserUses(this.selectedUser.id).then((data) => {
        this.userUses = data;
      });
    },
    saveUserUses() {
      const uses = {};
      this.editableServices.forEach((s) => {
        uses[s.field] = this.userUses[s.field];
      });
      serviceLogService
        .updateUserUses({ user: this.selectedUser.id, uses })
        .then((data) => {
          this.userUses = data;
          this.$toasted.success("Usos actualizados");
        })
        .catch(() => this.$toasted.error("No se pudo actualizar"));
    },
    grantAll() {
      serviceLogService
        .grantUsesAll(this.grant)
        .then((data) => {
          this.$toasted.success(`Usos regalados a ${data.updated} usuarios`);
        })
        .catch(() => this.$toasted.error("No se pudo regalar los usos"));
    },
  },
};
</script>
